import React, { useState, useEffect, useMemo } from 'react';
import { t } from '../lib/i18n';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import DealSection from '../components/DealSection';
import Footer from '../components/Footer';
import SiteWrapper from '../components/SiteWrapper';
import CategoryPicker from '../components/CategoryPicker';
import TopTenRow from '../components/TopTenRow';
import OfflineBanner from '../components/OfflineBanner';
import PromoCarousel from '../components/PromoCarousel';
import LiveBanner from '../components/LiveBanner';
import StoriesRow from '../components/StoriesRow';
import DownloadAllButton from '../components/DownloadAllButton';
import ProductResults, { useProductSearch } from '../components/ProductResults';
import { useOnline, getRecent } from '../lib/offline';
import { Settings2, Heart, MapPin, Loader2 } from 'lucide-react';
import { useGeo } from '../context/GeoContext';
import CartLoader, { useMinDelay } from '../components/CartLoader';
import { swrGet, sameData, SECTIONS } from '../lib/api';
import { sponsorTheme } from '../lib/liveThemes';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TABS = [
  { id: 'tous', label: 'Tous' },
  { id: 'pour-toi', label: 'Pour vous' },
  { id: 'jaime', label: 'Favoris' },
];

const SECTION_ICONS = { top: '🔥', weekly: '🏷️', flyers: '📰', recommended: '⭐' };
const TOP_LIMIT = 6;

// Rotation aléatoire par rubrique : nouvel ordre à chaque chargement de page
const shuffle = (list) => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const TabBar = ({ activeTab, setActiveTab, badges = {} }) => (
  <div className="flex items-center gap-8 py-3" data-testid="tab-bar">
    {TABS.map((tab) => (
      <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
        className={`relative pb-2 text-sm font-semibold transition-colors duration-200 ${activeTab === tab.id ? 'text-white' : 'text-[#808080] hover:text-[#B3B3B3]'}`}>
        {t(tab.label)}
        {badges[tab.id] > 0 && (
          <span data-testid={`tab-${tab.id}-badge`} title={`${badges[tab.id]} nouveau(x) catalogue(s)`}
            className="absolute -top-1.5 -right-4 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E50914] text-white text-[10px] font-black flex items-center justify-center shadow-[0_0_10px_rgba(229,9,20,0.6)] animate-pulse">
            {badges[tab.id] > 9 ? '9+' : badges[tab.id]}
          </span>
        )}
        {activeTab === tab.id && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#E50914]" />}
      </button>
    ))}
  </div>
);

const HOME_URLS = ['/catalogs', '/categories', '/habillages', '/promo-banners/active', '/settings/sections'];
const applyHome = (set, [c, cat, h, p, s]) => { set.catalogs(c); set.categories(cat); set.habillages(h); set.promoBanners(p || {}); set.sectionModes(s.modes); set.topTenEnabled(!!s.top_ten); };

const HomePage = () => {
  const { user, favorites: favoriteIds, openAuth, saveInterests, markPourToiSeen } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'tous');
  useEffect(() => { if (location.state?.tab) setActiveTab(location.state.tab); }, [location.state]);
  const [activeCategory, setActiveCategory] = useState(location.state?.category || null);
  useEffect(() => { if (location.state?.category) { setActiveCategory(location.state.category); window.scrollTo({ top: 0, behavior: 'smooth' }); } }, [location.state]);
  const [homeCache] = useState(() => { const hits = HOME_URLS.map((u) => swrGet(u).cached); return hits.every((h) => h != null) ? hits : null; });
  const [catalogs, setCatalogs] = useState(() => homeCache?.[0] || []);
  const [categories, setCategories] = useState(() => homeCache?.[1] || []);
  const [habillages, setHabillages] = useState(() => homeCache?.[2] || []);
  const [loading, setLoading] = useState(!homeCache);
  const isLoading = useMinDelay(loading);
  const categoryCounts = useMemo(() => catalogs.reduce((acc, c) => ({ ...acc, [c.category]: (acc[c.category] || 0) + 1 }), {}), [catalogs]);
  const geo = useGeo();
  const [nearYou, setNearYou] = useState(() => localStorage.getItem('dealgo_near_you') === '1' && !!localStorage.getItem('dealgo_geo'));
  const toggleNearYou = async () => {
    if (nearYou) { setNearYou(false); localStorage.setItem('dealgo_near_you', '0'); return; }
    const ok = geo.coords || await geo.locate();
    if (ok) { setNearYou(true); localStorage.setItem('dealgo_near_you', '1'); }
  };
  const byDistance = nearYou && geo.ready;
  const [searchQuery, setSearchQuery] = useState(location.state?.search || '');
  useEffect(() => { if (location.state?.search) setSearchQuery(location.state.search); }, [location.state]);
  const [promoBanners, setPromoBanners] = useState(() => homeCache?.[3] || {});
  const [sectionModes, setSectionModes] = useState(() => homeCache?.[4]?.modes || {});
  const [topTenEnabled, setTopTenEnabled] = useState(() => !!homeCache?.[4]?.top_ten);
  const [topTen, setTopTen] = useState(() => swrGet('/catalogs/top').cached || []);
  const [liveEvent, setLiveEvent] = useState(() => swrGet('/live').cached?.event || null);
  useEffect(() => { swrGet('/live').promise.then((d) => setLiveEvent(d?.event || null)).catch(() => {}); }, []);
  // Official sponsor of the running/teased DealRun Live event: its catalogs are pinned first in every section.
  const sponsor = useMemo(() => {
    const sp = liveEvent?.sponsor;
    if (!sp || !['live', 'teaser'].includes(liveEvent.mode)) return null;
    const th = sponsorTheme(liveEvent);
    return { brand_id: sp.id, name: sp.name, color: th.accent, on: th.on };
  }, [liveEvent]);
  const online = useOnline();
  const recent = useMemo(() => getRecent().map((r) => r.deal), [online]); // eslint-disable-line react-hooks/exhaustive-deps

  const [localInterests, setLocalInterests] = useState(() => JSON.parse(localStorage.getItem('dealrun_interests') || '[]'));
  const [editingInterests, setEditingInterests] = useState(false);
  const [draftInterests, setDraftInterests] = useState([]);

  useEffect(() => {
    const set = { catalogs: (v) => setCatalogs((p) => (sameData(p, v) ? p : v)), categories: setCategories, habillages: setHabillages, promoBanners: setPromoBanners, sectionModes: setSectionModes, topTenEnabled: setTopTenEnabled };
    Promise.all(HOME_URLS.map((u) => swrGet(u).promise))
      .then((res) => applyHome(set, res))
      .catch(() => { if (!homeCache) toast.error('Impossible de charger les catalogues'); })
      .finally(() => setLoading(false));
    swrGet('/catalogs/top').promise.then((d) => setTopTen((p) => (sameData(p, d) ? p : d))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActiveCategory(null); }, [activeTab]);
  useEffect(() => { const timer = setTimeout(() => import('./CatalogPage').catch(() => {}), 2000); return () => clearTimeout(timer); }, []);

  const interests = user ? user.interests : localInterests;
  const followed = user?.followed_brands || [];
  const showPicker = editingInterests || (interests.length === 0 && followed.length === 0);

  // New catalogs since the last visit of "For you"
  const SEEN_KEY = 'dealrun_pour_toi_seen';
  const [seenSnapshot, setSeenSnapshot] = useState(undefined);
  const [visitedPourToi, setVisitedPourToi] = useState(false);
  useEffect(() => {
    if (user === undefined) return;
    setSeenSnapshot(user ? user.pour_toi_seen_at || null : localStorage.getItem(SEEN_KEY));
    setVisitedPourToi(false);
  }, [user?.id, user === undefined]); // eslint-disable-line react-hooks/exhaustive-deps

  const newIds = useMemo(() => {
    if (!seenSnapshot) return new Set();
    return new Set(catalogs.filter((c) => (followed.includes(c.brand_id) || interests.includes(c.category)) && c.created_at > seenSnapshot).map((c) => c.id));
  }, [catalogs, followed, interests, seenSnapshot]);

  useEffect(() => {
    if (activeTab !== 'pour-toi' || user === undefined) return;
    setVisitedPourToi(true);
    if (user) markPourToiSeen().catch(() => {});
    else localStorage.setItem(SEEN_KEY, new Date().toISOString());
  }, [activeTab, user === undefined, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabBadges = { 'pour-toi': visitedPourToi ? 0 : newIds.size };

  const startEditing = () => { setDraftInterests(interests); setEditingInterests(true); };
  const toggleDraft = (slug) => setDraftInterests((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));
  const validateInterests = async () => {
    if (user) {
      await saveInterests(draftInterests);
    } else {
      localStorage.setItem('dealrun_interests', JSON.stringify(draftInterests));
      setLocalInterests(draftInterests);
    }
    setEditingInterests(false);
  };

  const bySection = useMemo(() => {
    const filtered = activeCategory ? catalogs.filter((c) => c.category === activeCategory) : catalogs;
    const grouped = SECTIONS.reduce((acc, s) => {
      const list = filtered.filter((c) => (c.display_section || c.section) === s.id);
      const ordered = byDistance ? geo.sortByDistance(list) : sectionModes[s.id] === 'manual' ? list : shuffle(list);
      return { ...acc, [s.id]: sponsor ? [...ordered.filter((c) => c.brand_id === sponsor.brand_id), ...ordered.filter((c) => c.brand_id !== sponsor.brand_id)] : ordered };
    }, {});
    // TOP 6 is capped at 6: extra catalogs flow into "You may also like"
    if (grouped.top.length > TOP_LIMIT) {
      grouped.recommended = [...grouped.top.slice(TOP_LIMIT), ...grouped.recommended];
      grouped.top = grouped.top.slice(0, TOP_LIMIT);
    }
    return grouped;
  }, [catalogs, activeCategory, sectionModes, byDistance, geo, sponsor]);

  const followedDeals = useMemo(() => catalogs.filter((c) => followed.includes(c.brand_id)), [catalogs, followed]);
  const pourToi = useMemo(() => catalogs.filter((c) => interests.includes(c.category) && !followed.includes(c.brand_id)), [catalogs, interests, followed]);
  const favorites = useMemo(() => catalogs.filter((c) => favoriteIds.includes(c.id)), [catalogs, favoriteIds]);

  const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const query = searchQuery.trim();
  const productSearch = useProductSearch(query);
  const searchResults = useMemo(() => {
    if (!query) return [];
    const q = normalize(query);
    const seen = new Set();
    return catalogs.filter((c) => {
      const catName = categories.find((k) => k.slug === c.category)?.name || '';
      if (!normalize(c.brand).includes(q) && !normalize(catName).includes(q)) return false;
      if (seen.has(c.brand + c.images[0])) return false;
      seen.add(c.brand + c.images[0]);
      return true;
    });
  }, [catalogs, categories, query]);

  const pickerToggle = (slug) => { toggleDraft(slug); setEditingInterests(true); };

  return (
    <div className="min-h-screen bg-black" data-testid="homepage">
      <DownloadAllButton />
      <SiteWrapper habillages={habillages}>
        <Navbar onCategorySelect={setActiveCategory} activeCategory={activeCategory} categories={categories} categoryCounts={categoryCounts} searchQuery={searchQuery} onSearchChange={setSearchQuery} />

        <main className="px-3 pt-4 pb-8">
          <OfflineBanner />
          {!query && <LiveBanner />}
          {!query && <StoriesRow />}
          {!online && recent.length > 0 && (
            <div data-testid="recent-offline-section">
              <DealSection title="Vos catalogues récents" deals={recent} initialCount={9999} columns={4} />
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-x-4 border-b border-[#262626] mb-6">
            <TabBar activeTab={activeTab} setActiveTab={setActiveTab} badges={tabBadges} />
            <button onClick={toggleNearYou} aria-pressed={nearYou} data-testid="near-you-toggle" disabled={geo.status === 'locating'}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 mb-2 rounded-full text-xs font-bold border transition-colors ${nearYou ? 'bg-emerald-500 border-emerald-500 text-black' : 'border-[#262626] text-[#B3B3B3] hover:text-white hover:border-white/40'}`}>
              {geo.status === 'locating' ? <Loader2 size={13} className="animate-spin" /> : <MapPin size={13} />} {t('Près de chez vous')}
            </button>
            {geo.status === 'denied' && !nearYou && <span className="text-[11px] text-[#808080] mb-2" data-testid="near-you-denied">{t('Accès à la localisation refusé')}</span>}
          </div>

          {isLoading && (
            <div className="flex justify-center py-20" data-testid="loading-indicator">
              <CartLoader />
            </div>
          )}

          {!isLoading && query && (
            <div data-testid="search-results">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl font-bold text-white" data-testid="search-results-title">
                  {searchResults.length + productSearch.products.length} résultat{searchResults.length + productSearch.products.length !== 1 ? 's' : ''} pour « {query} »
                </h1>
                <button onClick={() => setSearchQuery('')} data-testid="search-results-clear"
                  className="text-sm font-semibold text-[#B3B3B3] hover:text-[#E50914] transition-colors">Effacer la recherche</button>
              </div>
              <ProductResults query={query} loading={productSearch.loading} products={productSearch.products} />
              {searchResults.length > 0 ? (
                <DealSection title="Catalogues trouvés" icon="🔎" deals={searchResults} initialCount={9999} columns={4} />
              ) : productSearch.loading || productSearch.products.length > 0 ? null : (
                <div className="text-center py-16" data-testid="search-no-results">
                  <p className="text-[#808080] text-lg">Aucun catalogue ne correspond à « {query} ».</p>
                </div>
              )}
            </div>
          )}

          {!isLoading && !query && activeTab === 'tous' && (
            <>
              <h1 className="text-2xl md:text-3xl font-black text-white text-center mb-8 tracking-tight" data-testid="main-title">
                {t('Tous les catalogues à La Réunion !')}
              </h1>

              {activeCategory && (
                <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-[#E50914]/10 border border-[#E50914]/30 rounded-sm w-fit" data-testid="active-filter">
                  <span className="text-sm text-[#E50914] font-medium">
                    Filtre : {t(categories.find((c) => c.slug === activeCategory)?.name || activeCategory)}
                  </span>
                  <button onClick={() => setActiveCategory(null)} className="text-[#E50914] hover:text-white text-sm font-bold" data-testid="clear-filter-btn">✕</button>
                </div>
              )}

              {SECTIONS.map((s, idx) => (
                <React.Fragment key={s.id}>
                  {(bySection[s.id].length > 0 || !activeCategory) && (
                    <DealSection title={s.label} icon={SECTION_ICONS[s.id]} deals={bySection[s.id]} sponsor={sponsor} shine={s.id === 'top' || s.id === 'weekly'}
                      initialCount={9999} columns={s.id === 'top' ? 3 : 4} />
                  )}
                  {idx === 0 && !activeCategory && topTenEnabled && <TopTenRow deals={topTen} />}
                  {promoBanners[s.id]?.banners?.length > 0 && <PromoCarousel banners={promoBanners[s.id].banners} placement={s.id} />}
                </React.Fragment>
              ))}
            </>
          )}

          {!isLoading && !query && activeTab === 'pour-toi' && (
            showPicker ? (
              <CategoryPicker categories={categories} selected={draftInterests} onToggle={pickerToggle} onValidate={validateInterests} />
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h1 className="text-2xl font-bold text-white" data-testid="pour-toi-title">{t('Vos catalogues personnalisés')}</h1>
                  <button onClick={startEditing} data-testid="modify-interests-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-sm border border-[#262626] text-sm font-medium text-[#B3B3B3] hover:border-[#E50914] hover:text-[#E50914] transition-colors">
                    <Settings2 size={16} /> {t('Modifier mes centres d’intérêt')}
                  </button>
                </div>
                {!user && (
                  <p className="text-xs text-[#808080] mb-4" data-testid="pour-toi-login-hint">
                    <button onClick={openAuth} className="text-[#E50914] hover:underline">Se connecter</button> pour conserver vos préférences sur tous vos appareils.
                  </p>
                )}
                {followedDeals.length > 0 && (
                  <div data-testid="followed-section">
                    <DealSection title="Enseignes que vous suivez" icon="🔔" deals={followedDeals} initialCount={9999} columns={4} newIds={newIds} />
                    <p className="text-xs text-[#808080] -mt-2 mb-2">
                      Vous suivez {followed.length} enseigne{followed.length > 1 ? 's' : ''} · <Link to="/magasins" className="text-[#E50914] hover:underline" data-testid="manage-follows-link">gérer depuis la page Magasins</Link>
                    </p>
                  </div>
                )}
                {followed.length > 0 && followedDeals.length === 0 && (
                  <p className="text-sm text-[#808080] mb-6" data-testid="followed-empty">{t('Les enseignes que vous suivez n’ont pas de catalogue en cours actuellement.')}</p>
                )}
                {pourToi.length > 0 ? (
                  <DealSection title="Sélection pour vous" icon="💝" deals={pourToi} initialCount={9999} columns={4} newIds={newIds} />
                ) : interests.length === 0 ? (
                  <div className="text-center py-10" data-testid="no-interests-message">
                    <p className="text-[#808080] mb-4">{t('Choisissez vos centres d’intérêt pour découvrir encore plus de catalogues.')}</p>
                    <button onClick={startEditing} className="px-6 py-2.5 rounded-sm bg-[#E50914] text-white text-sm font-bold hover:bg-[#F40612] transition-colors" data-testid="choose-interests-btn">{t('Choisir mes centres d’intérêt')}</button>
                  </div>
                ) : (
                  <div className="text-center py-16" data-testid="no-deals-message">
                    <p className="text-[#808080] text-lg mb-4">{t('Aucun autre catalogue ne correspond à vos centres d’intérêt actuellement.')}</p>
                  </div>
                )}
              </>
            )
          )}

          {!isLoading && !query && activeTab === 'jaime' && (
            <>
              {!user && favorites.length > 0 && (
                <p className="text-xs text-[#808080] mb-4" data-testid="jaime-login-hint">
                  <button onClick={openAuth} className="text-[#E50914] hover:underline" data-testid="jaime-login-btn">Se connecter</button> {t('pour conserver vos favoris sur tous vos appareils.')}
                </p>
              )}
              {favorites.length > 0 ? (
                <DealSection title="Mes favoris" icon="❤️" deals={favorites} initialCount={9999} columns={4} />
              ) : (
                <div className="text-center py-16" data-testid="jaime-empty">
                  <Heart size={40} className="mx-auto text-[#E50914] mb-4" />
                  <p className="text-[#808080] text-lg mb-2">Vos catalogues favoris apparaîtront ici</p>
                  <p className="text-[#808080]/60 text-sm">{t('Appuyez sur le cœur d’un catalogue pour l’ajouter à vos favoris')}</p>
                </div>
              )}
            </>
          )}
        </main>
      </SiteWrapper>
      <Footer />
    </div>
  );
};

export default HomePage;
